import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { resolveImage } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Building2, MapPin, Calendar, BadgeCheck, ArrowRight, ChevronLeft, ChevronRight, AlertTriangle, RotateCcw } from "lucide-react";
import AuthGate from "../components/AuthGate";
import { PropertyGridSkeleton } from "../components/Skeletons";
import { useDataSync } from "../lib/dataSync";
import ProjectFilterPills from "../components/ProjectFilterPills";
import ActiveFilterChips from "../components/ActiveFilterChips";
import { fetchProjectSchemaPublic, _resetProjectSchemaCache } from "../lib/projectSchemaApi";
// Phase I-B.2C — Indian price formatting + possession normalisation.
import { formatINRDisplay, normalisePossession, normaliseLegacyUnitPrice } from "../lib/projectDisplayHelpers";

// v2.87 Phase 2 (Sprint B) — Project Filters rebuild.
// Drops the "fetch ALL → render ALL" pattern. Now:
//   1. On mount, fetch /api/projects (legacy unfiltered) once → derive
//      builderOptions + locationOptions for the pill dropdowns.
//   2. On filter / page change → fetch /api/projects?paginated=true with
//      current filters → render that page's slice. URL stays in sync.
//   3. useDataSync('projects') → refetch both facets + current page.
//
// Phase I-B.1 — Status/BHK/Possession/Amenity dropdown vocabularies
// come from /api/project-schema/public (no hard-coded fallbacks). If
// the schema fetch fails, the page shows a clear retry banner instead
// of silently substituting fixtures.

// Phase I-B.1 — Presentation-only color map (the DB doesn't ship color
// metadata). Unknown statuses fall back to a neutral chip tone.
const STATUS_COLOR = {
  under_construction: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  ready_to_move: "bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]",
  launching_soon: "bg-[#EEF4FF] text-[#0044CC] border-[#C7D9FF]",
};
const STATUS_COLOR_FALLBACK = "bg-[#F4F4F5] text-[#3F3F46] border-[#E4E4E7]";

const DEFAULT_PRICE = [0, 999999999];
const DEFAULT_FILTERS = {
  q: "", status: "", location: "", builder_id: "", bhk: "",
  priceRange: DEFAULT_PRICE,
  possession_window: "", rera: false, sort: "",
  // v2.87 Phase 3b ext — Amenities multi-select (comma-separated).
  amenities: "",
};

// URL ⇄ state helpers — keep filter contract crisp.
const filtersFromQuery = (sp) => {
  const f = { ...DEFAULT_FILTERS };
  if (sp.get("q"))            f.q = sp.get("q");
  if (sp.get("status"))       f.status = sp.get("status");
  if (sp.get("location"))     f.location = sp.get("location");
  if (sp.get("builder_id"))   f.builder_id = sp.get("builder_id");
  if (sp.get("bhk"))          f.bhk = sp.get("bhk");
  if (sp.get("possession_window")) f.possession_window = sp.get("possession_window");
  if (sp.get("rera") === "true")   f.rera = true;
  if (sp.get("sort"))         f.sort = sp.get("sort");
  if (sp.get("amenities"))    f.amenities = sp.get("amenities");
  const minP = sp.get("min_price"), maxP = sp.get("max_price");
  if (minP || maxP) f.priceRange = [Number(minP || 0), Number(maxP || 999999999)];
  return f;
};
const filtersToQuery = (f) => {
  const p = new URLSearchParams();
  if (f.q)              p.set("q", f.q);
  if (f.status)         p.set("status", f.status);
  if (f.location)       p.set("location", f.location);
  if (f.builder_id)     p.set("builder_id", f.builder_id);
  if (f.bhk)            p.set("bhk", f.bhk);
  if (f.possession_window) p.set("possession_window", f.possession_window);
  if (f.rera)           p.set("rera", "true");
  if (f.sort)           p.set("sort", f.sort);
  if (f.amenities)      p.set("amenities", f.amenities);
  if (f.priceRange && !(f.priceRange[0] === 0 && f.priceRange[1] >= 999999999)) {
    if (f.priceRange[0] > 0) p.set("min_price", String(f.priceRange[0]));
    if (f.priceRange[1] < 999999999) p.set("max_price", String(f.priceRange[1]));
  }
  return p.toString();
};

export default function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => filtersFromQuery(searchParams));
  const [page, setPage] = useState(() => Number(searchParams.get("page") || 1));
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ pagination: { total: 0, total_pages: 0, has_next: false, has_prev: false } });
  const [facets, setFacets] = useState({ builders: [], locations: [], amenities: [] });
  const [loading, setLoading] = useState(true);
  const [gate, setGate] = useState(null);
  // Phase I-B.1 — Project schema state (drives status / BHK / possession /
  // amenities dropdowns). NO hard-coded fallback. If `schemaError` is
  // set we render a retry banner instead of the filter pills.
  const [schema, setSchema] = useState(null);
  const [schemaError, setSchemaError] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth(); // eslint-disable-line no-unused-vars

  // Phase I-B.1 — Load /api/project-schema/public exactly once on mount
  // and after every useDataSync('config') ping (so admin edits flow in).
  const loadSchema = useCallback(async () => {
    setSchemaError(null);
    try {
      _resetProjectSchemaCache();
      const s = await fetchProjectSchemaPublic({ force: true });
      setSchema(s);
    } catch (e) {
      setSchemaError(e?.response?.data?.detail || e?.message || "Could not load project schema");
    }
  }, []);
  useEffect(() => { loadSchema(); }, [loadSchema]);
  useDataSync(["config"], loadSchema);

  // Phase I-B.1 — Derive dropdown vocabularies from the schema. Empty
  // arrays are fine (we render an "empty" state inside the dropdown)
  // but the parent NEVER falls back to a hard-coded list.
  const statusOptions = useMemo(() => {
    if (!schema) return [];
    return (schema.catalog_options || [])
      .filter((o) => o.scope === "project_status")
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((o) => ({ key: o.key, label: o.label }));
  }, [schema]);
  const bhkOptions = useMemo(() => {
    if (!schema) return [];
    return (schema.catalog_options || [])
      .filter((o) => o.scope === "configuration_type")
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((o) => o.label);
  }, [schema]);
  const possessionOptions = useMemo(() => {
    if (!schema) return [];
    return (schema.catalog_options || [])
      .filter((o) => o.scope === "possession_window")
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((o) => ({ value: o.key, label: o.label }));
  }, [schema]);
  // Amenity vocabulary: ALWAYS comes from the schema (Phase I-B.1
  // explicitly bans the hard-coded STANDARD_AMENITIES fallback). The
  // backend admin maintains this list under the "amenity" scope.
  const schemaAmenityLabels = useMemo(() => {
    if (!schema) return [];
    return (schema.catalog_options || [])
      .filter((o) => o.scope === "amenity")
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
      .map((o) => o.label);
  }, [schema]);
  // Map status key → display label for card / chip rendering.
  const statusLabelMap = useMemo(() => {
    const m = {};
    for (const o of statusOptions) m[o.key] = o.label;
    return m;
  }, [statusOptions]);
  // Map possession value → display label for chip rendering.
  const possessionLabelMap = useMemo(() => {
    const m = {};
    for (const o of possessionOptions) m[o.value] = o.label;
    return m;
  }, [possessionOptions]);

  // Sync URL ⇄ filters/page (replace so back-button gets nice flat history).
  useEffect(() => {
    const qs = filtersToQuery(filters);
    const next = new URLSearchParams(qs);
    if (page > 1) next.set("page", String(page));
    const nextStr = next.toString();
    if (nextStr !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, page, searchParams, setSearchParams]);

  // Derive params payload for the /api/projects?paginated=true call.
  const apiParams = useMemo(() => {
    const p = { paginated: true, page, page_size: 24 };
    if (filters.q) p.q = filters.q;
    if (filters.status) p.status = filters.status;
    if (filters.location) p.location = filters.location;
    if (filters.builder_id) p.builder_id = filters.builder_id;
    if (filters.bhk) p.bhk = filters.bhk;
    if (filters.priceRange && filters.priceRange[0] > 0) p.min_price = filters.priceRange[0];
    if (filters.priceRange && filters.priceRange[1] < 999999999) p.max_price = filters.priceRange[1];
    if (filters.possession_window) p.possession_window = filters.possession_window;
    if (filters.rera) p.rera = true;
    if (filters.sort) p.sort = filters.sort;
    if (filters.amenities) p.amenities = filters.amenities;
    return p;
  }, [filters, page]);

  // Fetch the current filtered + paginated slice.
  const loadList = useCallback(() => {
    setLoading(true);
    api.get("/projects", { params: apiParams }).then((r) => {
      // Envelope shape: { success, data, meta }
      setData(r.data?.data || []);
      setMeta(r.data?.meta || { pagination: { total: 0 } });
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [apiParams]);

  // Fetch the unfiltered list once for facets (builder + location + amenity dropdowns).
  const loadFacets = useCallback(() => {
    api.get("/projects").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      const builders = new Map();
      const locations = new Set();
      const discoveredAmenities = new Set();
      for (const p of list) {
        if (p.created_by && p.builder_name) builders.set(p.created_by, p.builder_name);
        else if (p.assigned_to_user_id && p.builder_name) builders.set(p.assigned_to_user_id, p.builder_name);
        if (p.location) locations.add(p.location);
        if (p.main_area) locations.add(p.main_area);
        for (const a of (p.amenities || [])) {
          const t = String(a || "").trim();
          if (t) discoveredAmenities.add(t);
        }
      }
      setFacets({
        builders: Array.from(builders.entries()).map(([id, name]) => ({ id, name })),
        locations: Array.from(locations).sort(),
        // Phase I-B.1 — Raw discovered amenities only. The Project-Schema
        // catalog list is merged in at render-time (see `amenityOptions`
        // useMemo below) so admin-curated additions surface even before
        // any project uses them.
        amenities: Array.from(discoveredAmenities).sort(),
      });
    }).catch(() => { /* facets are optional — pill picker still works without */ });
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadFacets(); }, [loadFacets]);
  useDataSync(["projects"], () => { loadList(); loadFacets(); });

  // Phase I-B.1 — Merge schema-curated amenities with project-derived
  // ones so admin additions surface immediately even before any
  // project uses them. NO hard-coded fallback list.
  const amenityOptions = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const a of schemaAmenityLabels) {
      if (a && !seen.has(a)) { seen.add(a); out.push(a); }
    }
    for (const a of (facets.amenities || [])) {
      if (a && !seen.has(a)) { seen.add(a); out.push(a); }
    }
    return out;
  }, [schemaAmenityLabels, facets.amenities]);

  // Reset page to 1 when filters change.
  useEffect(() => { setPage(1); }, [JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  const onReset = useCallback(() => { setFilters(DEFAULT_FILTERS); setPage(1); }, []);

  // v2.87 Phase 3a — Build active-filter chips for the strip above the grid.
  const buildProjectChips = () => {
    const chips = [];
    if (filters.q) {
      chips.push({ key: "q", label: `"${filters.q}"`, onRemove: () => setFilters((p) => ({ ...p, q: "" })) });
    }
    // Status (multi-select via comma) — labels resolved from schema.
    String(filters.status || "").split(",").map((x) => x.trim()).filter(Boolean).forEach((s) => {
      chips.push({
        key: "status", value: s,
        label: statusLabelMap[s] || s,
        onRemove: () => {
          const arr = String(filters.status || "").split(",").map((x) => x.trim()).filter(Boolean);
          setFilters((p) => ({ ...p, status: arr.filter((x) => x !== s).join(",") }));
        },
      });
    });
    if (filters.location) {
      chips.push({ key: "location", label: filters.location, onRemove: () => setFilters((p) => ({ ...p, location: "" })) });
    }
    // Builder (multi-select)
    String(filters.builder_id || "").split(",").map((x) => x.trim()).filter(Boolean).forEach((bid) => {
      const name = facets.builders.find((b) => b.id === bid)?.name || bid;
      chips.push({
        key: "builder", value: bid,
        label: `Builder: ${name}`,
        onRemove: () => {
          const arr = String(filters.builder_id || "").split(",").map((x) => x.trim()).filter(Boolean);
          setFilters((p) => ({ ...p, builder_id: arr.filter((x) => x !== bid).join(",") }));
        },
      });
    });
    // BHK (multi-select)
    String(filters.bhk || "").split(",").map((x) => x.trim()).filter(Boolean).forEach((bhk) => {
      chips.push({
        key: "bhk", value: bhk, label: bhk,
        onRemove: () => {
          const arr = String(filters.bhk || "").split(",").map((x) => x.trim()).filter(Boolean);
          setFilters((p) => ({ ...p, bhk: arr.filter((x) => x !== bhk).join(",") }));
        },
      });
    });
    // Price range
    const pr = filters.priceRange;
    if (pr && !(pr[0] === 0 && pr[1] >= 999999999)) {
      const fmt = (n) => n >= 10000000 ? `₹${(n / 10000000).toFixed(n % 10000000 === 0 ? 0 : 1)} Cr` :
                        n >= 100000 ? `₹${(n / 100000).toFixed(n % 100000 === 0 ? 0 : 1)} L` : `₹${n}`;
      chips.push({
        key: "price",
        label: pr[0] === 0 ? `Up to ${fmt(pr[1])}` : `${fmt(pr[0])} – ${fmt(pr[1])}`,
        onRemove: () => setFilters((p) => ({ ...p, priceRange: DEFAULT_PRICE })),
      });
    }
    if (filters.possession_window) {
      chips.push({
        key: "possession",
        label: possessionLabelMap[filters.possession_window] || filters.possession_window,
        onRemove: () => setFilters((p) => ({ ...p, possession_window: "" })),
      });
    }
    if (filters.rera) {
      chips.push({ key: "rera", label: "RERA approved", onRemove: () => setFilters((p) => ({ ...p, rera: false })) });
    }
    // v2.87 Phase 3b ext — Amenities (multi-select).
    String(filters.amenities || "").split(",").map((x) => x.trim()).filter(Boolean).forEach((a) => {
      chips.push({
        key: "amenities", value: a, label: a,
        onRemove: () => {
          const arr = String(filters.amenities || "").split(",").map((x) => x.trim()).filter(Boolean);
          setFilters((p) => ({ ...p, amenities: arr.filter((x) => x !== a).join(",") }));
        },
      });
    });
    if (filters.sort) {
      const sortLabels = { latest: "Latest", price_asc: "Price ↑", price_desc: "Price ↓", possession_soonest: "Possession ↑" };
      chips.push({
        key: "sort", label: `Sort: ${sortLabels[filters.sort] || filters.sort}`,
        onRemove: () => setFilters((p) => ({ ...p, sort: "" })),
      });
    }
    return chips;
  };

  const total = meta.pagination?.total || 0;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="projects-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12">
        {/* Heading */}
        <div className="max-w-2xl mb-8">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#0055FF] mb-3">
            <Building2 size={12} className="inline mb-0.5 mr-1" /> Projects · New Construction
          </div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight text-[#09090B]">
            Builder / Owner projects, curated &amp; verified
          </h1>
          <p className="text-[15px] text-[#52525B] mt-3">
            Multiple unit configurations, House Entry approved. {total > 0 && <span className="font-semibold text-[#09090B]">· {total} projects</span>}
          </p>
        </div>

        {/* Sticky filter strip */}
        <div
          className="sticky top-[48px] md:top-[52px] lg:top-[56px] z-20 bg-[#FAFAFA] -mx-4 px-4 md:mx-0 md:px-0 pt-1 pb-2 mb-2 border-b border-slate-100"
          data-results-anchor="filter-strip"
        >
          {schemaError ? (
            <div
              className="bg-red-50 border border-red-200 rounded-sm px-3 py-3 text-[12px] text-red-800 flex items-start gap-2"
              data-testid="projects-schema-error"
            >
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold">Could not load project filter options</div>
                <div className="text-red-700 mt-0.5">{schemaError}</div>
              </div>
              <button
                type="button"
                onClick={loadSchema}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-red-300 bg-white text-[12px] font-semibold text-red-700 hover:bg-red-50 shrink-0"
                data-testid="projects-schema-retry"
              >
                <RotateCcw size={12} /> Retry
              </button>
            </div>
          ) : (
            <ProjectFilterPills
              filters={filters}
              setFilters={setFilters}
              onReset={onReset}
              builderOptions={facets.builders}
              locationOptions={facets.locations}
              amenityOptions={amenityOptions}
              statusOptions={statusOptions}
              bhkOptions={bhkOptions}
              possessionOptions={possessionOptions}
              testidPrefix="pfp"
            />
          )}
        </div>

        {/* v2.87 Phase 3a — Active filter chips strip */}
        <ActiveFilterChips
          chips={buildProjectChips()}
          onClearAll={onReset}
          testidPrefix="afc-proj"
        />

        {loading && <PropertyGridSkeleton count={6} />}
        {!loading && data.length === 0 && (
          <div className="text-center py-16 bg-white border border-[#E4E4E7] rounded-sm" data-testid="projects-empty">
            <Building2 size={40} className="mx-auto text-[#A1A1AA] mb-3" />
            <h3 className="font-heading font-semibold text-lg">No projects match your filters</h3>
            <p className="text-[13px] text-[#52525B] mt-1">Try clearing some filters or pick a different status.</p>
            <button onClick={onReset} className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0055FF] text-white text-[12.5px] font-semibold hover:bg-[#0044CC]" data-testid="projects-empty-reset">
              Reset filters
            </button>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="projects-grid">
          {data.map((p) => {
            const img = p.images && p.images[0] ? resolveImage(p.images[0], { w: 600 }) : null;
            // Legacy units have mixed unit semantics — some store lakhs as small
            // integers, others store full INR. `normaliseLegacyUnitPrice` smooths
            // this out so the card always shows a sensible Indian amount.
            const minPrice = Math.min(
              ...(p.units || [])
                .map((u) => normaliseLegacyUnitPrice(u.price_min))
                .filter((x) => x && x > 0)
            );
            const bhks = [...new Set((p.units || []).map((u) => u.bhk).filter(Boolean))];
            return (
              <Link
                to={`/project/${p.id}`}
                key={p.id}
                className="group bg-white border border-[#E4E4E7] rounded-sm overflow-hidden rise hover:border-[#0055FF] transition-colors"
                data-testid={`project-card-${p.id}`}
              >
                <div className="aspect-[16/10] bg-[#F4F4F5] relative overflow-hidden">
                  {img ? (
                    <img loading="lazy" src={img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#A1A1AA]"><Building2 size={48} strokeWidth={1} /></div>
                  )}
                  <span className={`absolute top-3 left-3 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 border rounded-sm ${STATUS_COLOR[p.status] || STATUS_COLOR_FALLBACK}`}>
                    {statusLabelMap[p.status] || p.status}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-semibold text-[16px] text-[#09090B]">{p.name}</h3>
                  <div className="text-[12px] text-[#52525B] flex items-center gap-1 mt-0.5">
                    <MapPin size={11} /> {p.location || "—"}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {bhks.slice(0, 4).map((b) => (
                      <span key={b} className="text-[10px] uppercase tracking-wider font-semibold bg-[#F4F4F5] border border-[#E4E4E7] px-2 py-0.5 rounded-sm">{b}</span>
                    ))}
                  </div>
                  <div className="flex items-end justify-between pt-3 mt-3 border-t border-[#F4F4F5]">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold">Starting</div>
                      <div className="font-heading font-bold text-lg text-[#0055FF] font-mono-data leading-none mt-0.5" data-testid={`project-card-${p.id}-price`}>
                        {Number.isFinite(minPrice) && minPrice > 0 ? formatINRDisplay(minPrice) : "—"}
                      </div>
                    </div>
                    {p.expected_possession && (
                      <div className="text-right">
                        <div className="text-[10px] uppercase tracking-wider text-[#A1A1AA] font-semibold">Possession</div>
                        <div className="text-[12px] font-semibold text-[#09090B] font-mono-data inline-flex items-center gap-1" data-testid={`project-card-${p.id}-possession`}>
                          <Calendar size={11} /> {normalisePossession(p.expected_possession).label}
                        </div>
                      </div>
                    )}
                  </div>
                  {p.rera_number && (
                    <div className="text-[10px] text-[#15803D] font-semibold inline-flex items-center gap-1 mt-3" data-testid={`project-card-rera-${p.id}`}>
                      <BadgeCheck size={11} /> RERA: {p.rera_number}
                    </div>
                  )}
                  <div className="text-[11px] font-semibold text-[#0055FF] mt-2 inline-flex items-center gap-1">
                    View Details <ArrowRight size={11} />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Pagination */}
        {!loading && (meta.pagination?.total_pages || 0) > 1 && (
          <div className="flex items-center justify-center gap-3 mt-10" data-testid="projects-pagination">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!meta.pagination?.has_prev}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-200 bg-white text-[12.5px] font-semibold disabled:opacity-40 hover:border-[#0055FF]/40 disabled:hover:border-slate-200"
              data-testid="projects-pagination-prev"
            >
              <ChevronLeft size={13} /> Previous
            </button>
            <span className="text-[12.5px] text-slate-600 font-mono-data" data-testid="projects-pagination-info">
              Page {page} of {meta.pagination?.total_pages || 1}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!meta.pagination?.has_next}
              className="inline-flex items-center gap-1 px-4 py-2 rounded-full border border-slate-200 bg-white text-[12.5px] font-semibold disabled:opacity-40 hover:border-[#0055FF]/40 disabled:hover:border-slate-200"
              data-testid="projects-pagination-next"
            >
              Next <ChevronRight size={13} />
            </button>
          </div>
        )}
      </div>
      <AuthGate open={!!gate} onClose={() => setGate(null)} action="details" returnTo={gate || ""} />
    </div>
  );
}
